import { type ReactNode } from "react";
import {
  isNotFound,
  isRedirect,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { type UseQueryResult } from "@tanstack/react-query";
import { requestUnavailable } from "@softmax/observatory-client/transport";

import { useActualViewer } from "@softmax/web-features/session/records";

import { PlayPending } from "./play-pending";

function PlayError({ error, reset }: ErrorComponentProps) {
  return (
    <main id="main" className="mx-auto max-w-4xl px-6 py-16">
      <h1 className="text-2xl font-semibold">Play is unavailable</h1>
      <p role="alert" className="mt-3">
        {error instanceof Error
          ? error.message
          : "This page could not be loaded."}
      </p>
      <button type="button" className="mt-4 underline" onClick={reset}>
        Try again
      </button>
    </main>
  );
}

export function PlayResource<T>({
  query,
  children,
  requiresSession = false,
}: {
  readonly requiresSession?: boolean;
  readonly query: UseQueryResult<T>;
  readonly children: (data: T) => ReactNode;
}) {
  const viewer = useActualViewer();

  if (isNotFound(query.error) || isRedirect(query.error)) throw query.error;

  if (requiresSession && viewer.isError && !viewer.data)
    return (
      <PlayError
        error={viewer.error}
        reset={() => {
          void viewer.refetch();
        }}
      />
    );

  const denied = requestUnavailable(query.error);

  if (query.data === undefined || denied) {
    if (query.error)
      return (
        <PlayError
          error={query.error}
          reset={() => {
            void query.refetch();
          }}
        />
      );

    return <PlayPending />;
  }

  return (
    <>
      {(query.error || (requiresSession && viewer.isError)) && (
        <div role="alert" className="mx-auto max-w-4xl px-6 py-3">
          Refresh failed. Your last result is shown.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => {
              if (viewer.isError) void viewer.refetch();

              if (query.isError) void query.refetch();
            }}
          >
            Try again
          </button>
        </div>
      )}
      <div
        style={{ display: "contents" }}
        inert={requiresSession && viewer.isError}
      >
        {children(query.data)}
      </div>
    </>
  );
}
